import { Box, Column } from "@stenajs-webui/core";
import {
  ActionMenuItem,
  FlatButton,
  stenaEyeShow,
  stenaMobile,
} from "@stenajs-webui/elements";
import { useSelector } from "react-redux";

import { FreightPortalPopover } from "../../../../../common-ui/popover/FreightPortalPopover";
import type { DetailedBooking } from "../../../../../common/graphql/fragments/gql/DetailedBooking";
import { useAppNavigate } from "../../../../../common/hooks/useAppNavigate";
import { modalRouteCreators } from "../../../../../common/routes/AppRoutes";
import { trackEvent } from "../../../../../common/tracking/trackerService";
import { useUpdateSubmit } from "../../../../../common/update-information/hooks/useUpdateSubmit";
import { getFormIsEdited } from "../../../../../services/update-form/updateFormReducer";
import type { StoreState } from "../../../../../store/storeState";
import { ShareBookingPanel } from "../../../share/components/ShareBookingPanel";
import { shouldShowShareBooking } from "../../../share/util/shareUtil";
import { BookingActionMenuButton } from "../BookingActionMenuButton";

interface BookingDetailsEditBookingActionsProps {
  booking: DetailedBooking;
}

export function BookingDetailsEditBookingActions({
  booking,
}: BookingDetailsEditBookingActionsProps) {
  const { submitting } = useUpdateSubmit({
    bookingNo: booking.bookingNo,
    formId: "Details",
  });

  const { push } = useAppNavigate();

  const edited = useSelector((state: StoreState) =>
    getFormIsEdited(state, { bookingNo: booking.bookingNo, formId: "Details" }),
  );

  const vehicleRegNumber = booking.vehicleRegNo || booking.trailerRegNo;

  const onClickViewBooking = (close: () => void) => {
    push(
      modalRouteCreators.bookingDetailsView({ bookingNo: booking.bookingNo }),
    );
    trackEvent({ category: "Details", action: "View booking" });
    close();
  };

  return (
    <Column
      width={"100%"}
      alignItems={["stretch", null, "flex-end"]}
      flexDirection={["column", null, "row"]}
      justifyContent={"flex-end"}
      gap
    >
      {shouldShowShareBooking(
        vehicleRegNumber,
        booking.bookingStatus.status,
        booking.bookingQuayStatus?.status,
      ) && (
        <FreightPortalPopover
          onOpened={() =>
            trackEvent({
              category: "Details",
              action: "Share booking (button)",
            })
          }
          placement={"bottom-end"}
          renderTrigger={(props) => (
            <Box display={["none", null, null, null, "flex"]}>
              <FlatButton
                leftIcon={stenaMobile}
                label={"Send to Driver app"}
                {...props}
              />
            </Box>
          )}
        >
          {({ onRequestClose }) => (
            <ShareBookingPanel
              onClose={onRequestClose}
              bookingNo={booking.bookingNo}
              vehicleRegNo={booking.vehicleRegNo}
              trackerCategory={"Details"}
              showCopyBookingLink={true}
            />
          )}
        </FreightPortalPopover>
      )}
      <BookingActionMenuButton
        booking={booking}
        disabled={submitting}
        edited={edited}
        renderItems={(close) => {
          return (
            <ActionMenuItem
              onClick={() => onClickViewBooking(close)}
              leftIcon={stenaEyeShow}
              label={"View booking"}
            />
          );
        }}
      />
    </Column>
  );
}
